import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeExternalLinks from 'rehype-external-links';
import remarkGfm from 'remark-gfm';
import { useAppStore } from "../../../../../../GlobalStore/GlobalStore.ts";
import CodeBlock from './components/CodeBlock';
import LaTeXBlock from './components/LaTeXBlock';
import CustomLink from './components/CustomLink';
import TableComponent from './components/TableComponent';
import {UnorderedList, OrderedList, ListItem, Heading1, Heading2, Heading3, Heading4, Heading5, Heading6, Blockquote, Paragraph, TableHead, TableHeader, TableBody, TableCell} from './components/MarkdownComponents';
import MermaidBlock from "./components/MermaidBlock.tsx";
import MarkdownImage from "./components/MarkdownImage.tsx";
import remarkWindowsPaths from './utils/remarkWindowsPaths';

interface TextProps {
    item: any;
    index: number;
}
const Text: React.FC<TextProps> = ({ item, index }) => {
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);

    const handleImageClick = (imageSource: string) => {
        setDynamicStateObj({
            visiblePopup: "enlargedContent",
            enlargedContent: {type: "image", content: imageSource},
        });
    };

    const handleCopy = (event: React.ClipboardEvent<HTMLElement>) => {
        event.preventDefault();
        const selection = window.getSelection();
        if (selection && selection.toString()) {
            navigator.clipboard.writeText(selection.toString());
        } else {
            const content = event.currentTarget.textContent || '';
            navigator.clipboard.writeText(content);
        }
    };

    return (
        <div className="react-md whitespace-pre-wrap p-0.5">
            <ReactMarkdown
                components={{
                    code({ className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || '');
                        const language = match ? match[1] : 'plaintext';
                        const isMultiLineCodeBlock = String(children).includes('\n');

                        if (language === 'latex' || language === 'math') {
                            return <LaTeXBlock index={index}>{String(children)}</LaTeXBlock>;
                        }
                        else if (language === 'mermaid') {
                            return <MermaidBlock index={index}>{String(children)}</MermaidBlock>;
                        }

                        return isMultiLineCodeBlock ? (
                            <CodeBlock
                                language={language}
                                index={index}
                            >
                                {String(children)}
                            </CodeBlock>
                        ) : (
                            <code className={`${className}`} {...props}>
                                {children}
                            </code>
                        );
                    },
                    a: ({ children, href, ...props }) => {
                        return (
                            <CustomLink
                                href={href}
                                {...props}
                            >
                                {children}
                            </CustomLink>
                        );
                    },
                    ul: UnorderedList,
                    ol: OrderedList,
                    li: ({ ...props }) => (
                        <ListItem handleCopy={handleCopy} {...props} />
                    ),
                    h1: ({ ...props }) => (
                        <Heading1 handleCopy={handleCopy} {...props} />
                    ),
                    h2: ({ ...props }) => (
                        <Heading2 handleCopy={handleCopy} {...props} />
                    ),
                    h3: ({ ...props }) => (
                        <Heading3 handleCopy={handleCopy} {...props} />
                    ),
                    h4: ({ ...props }) => (
                        <Heading4 handleCopy={handleCopy} {...props} />
                    ),
                    h5: ({ ...props }) => (
                        <Heading5 handleCopy={handleCopy} {...props} />
                    ),
                    h6: ({ ...props }) => (
                        <Heading6 handleCopy={handleCopy} {...props} />
                    ),
                    blockquote: ({ ...props }) => (
                        <Blockquote handleCopy={handleCopy} {...props} />
                    ),
                    table: ({ children, ...props }) => (
                        <TableComponent index={index} {...props}>
                            {children}
                        </TableComponent>
                    ),
                    thead: TableHead,
                    th: TableHeader,
                    tbody: TableBody,
                    td: TableCell,
                    p: ({ children, ...props }) => (
                        <Paragraph handleCopy={handleCopy} {...props}>
                            {children}
                        </Paragraph>
                    ),
                    img: ({ ...props }) => (
                        <MarkdownImage
                            src={props.src}
                            alt={props.alt}
                            onClick={handleImageClick}
                        />
                    )
                }}
                remarkPlugins={[remarkGfm, remarkWindowsPaths]}
                rehypePlugins={[
                    [rehypeExternalLinks, {target: '_blank', rel: ['noreferrer'] }]
                ]}
            >
                {item.text}
            </ReactMarkdown>
        </div>
    );
};

export default Text;